import React from 'react';

import { ProtectorType } from 'core/constants';
import { EnumOptions } from 'utils/api/modules/entities/entities.types';

import { ErrorText, OptionButton, OptionGroup } from './styles';

export const INLINE_OPTION_LIMIT = 6;

export type EntitiesInlineChoiceFieldProps = {
  type: ProtectorType;
  value: string[];
  items: EnumOptions;
  onChange: (value: string[]) => void;
  requiredNotFilled?: boolean;
  errorMessage?: string;
  optionTone?: Record<string, string>;
  /** The group's accessible name — the options carry no visible legend. */
  description?: string;
};

const EntitiesInlineChoiceField = ({
  type,
  value,
  items,
  onChange,
  requiredNotFilled,
  errorMessage,
  optionTone,
  description,
}: EntitiesInlineChoiceFieldProps): React.ReactElement => {
  const multiSelect = type === ProtectorType.MultiChoice;

  // Autosave is debounced 1000 ms, so the prop lags the click: without local state a second click
  // inside that window recomputes from the stale prop and loses the first answer.
  const [selection, setSelection] = React.useState<string[]>(value || []);

  // Reconciled on contents, not identity: the store replaces `data` wholesale on every autosave
  // response, including another question's, so identity churns constantly.
  const propKeys = (value || []).join(' ');
  React.useEffect(() => {
    setSelection((current) => (propKeys === current.join(' ') ? current : propKeys.split(' ').filter(Boolean)));
  }, [propKeys]);

  const selected = new Set(selection);

  const toggle = (key: string): void => {
    let next: string[];
    if (!multiSelect) {
      next = selected.has(key) ? [] : [key];
    } else {
      next = selected.has(key) ? selection.filter((item) => item !== key) : [...selection, key];
    }

    setSelection(next);
    onChange(next);
  };

  // Clearing a required answer deletes the stored field error, so without this it goes red silently.
  const missingRequired = Boolean(requiredNotFilled) && selection.length === 0;

  return (
    <>
      <OptionGroup role={multiSelect ? 'group' : 'radiogroup'} aria-label={description}>
        {(items || []).map((item) => (
          <OptionButton
            key={item.key}
            type="button"
            role={multiSelect ? 'checkbox' : 'radio'}
            aria-checked={selected.has(item.key)}
            data-testid="audit-inline-choice-option"
            data-option-key={item.key}
            $selected={selected.has(item.key)}
            $tone={optionTone?.[item.key]}
            $error={missingRequired || !!errorMessage}
            onClick={() => toggle(item.key)}
          >
            {item.value}
          </OptionButton>
        ))}
      </OptionGroup>
      {(errorMessage || missingRequired) && <ErrorText>{errorMessage || 'This field is required'}</ErrorText>}
    </>
  );
};

export default EntitiesInlineChoiceField;
